import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { type Observable, finalize, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';

import type { DirectoryNode } from '@mtm/drive/core';

import { LoadingSpinnerComponent } from '../../../../../shared/components/loading-spinner/loading-spinner.component';
import {
  DriveProjectStructureService,
  type ReorderChildInput,
} from '../../../../../shared/services/drive-project-structure.service';
import { ProjectSectionService } from '../../../../../shared/services/project-section.service';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { OverlayService } from '../../../../../shared/services/overlayService';
import { TranslatePipe } from '../../../../../shared/pipes/translate.pipe';
import type { CreativeBlueprintNode } from '../../../../../shared/interfaces/project';
import {
  type StageSection,
  directoryLabel,
  mergeStageSections,
  normalizeStageName,
} from './stage-sections.utils';

/** An assigned sub-section shown in the box; `pending` covers the create round-trip. */
interface AssignedSubsection {
  readonly directoryUuid: string; // '' while a create is in flight
  readonly name: string;
  readonly pending: boolean;
}

const EXCLUDED_TEMPLATE_KEYS = new Set(['MAKE-UP', 'HAIR_DRESSER']);

/**
 * Drive-mode variant of the project-stage panel. Same drag-and-drop UX as the
 * legacy `ProjectStagesComponent`, but sections/sub-sections are sourced from
 * and persisted to Drive (via {@link DriveProjectStructureService}) — never the
 * legacy `/api/projects/{id}/sections…` tables. The section tabs and the
 * available-template pool come from the creative blueprint, merged with the
 * project's Drive sections (see {@link mergeStageSections}): the backend no
 * longer creates section directories with the project, so a missing section is
 * created when its first sub-section is dropped in. Reordering the assigned box
 * is persisted via the drive children/reorder endpoint (`displayOrder`), so a
 * manual order survives reload and re-selecting the section.
 */
@Component({
  selector: 'mtm-drive-project-stages',
  standalone: true,
  imports: [DragDropModule, LoadingSpinnerComponent],
  providers: [TranslatePipe],
  templateUrl: './drive-project-stages.component.html',
  styleUrl: './drive-project-stages.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriveProjectStagesComponent {
  private readonly facade = inject(DriveProjectStructureService);
  private readonly projectSectionService = inject(ProjectSectionService);
  private readonly notificationService = inject(NotificationService);
  private readonly overlayService = inject(OverlayService);
  private readonly translate = inject(TranslatePipe);
  private readonly destroyRef = inject(DestroyRef);

  /** Project root directory uuid (`project.driveDirectoryUuid`). */
  readonly rootId = input.required<string>();
  readonly projectId = input.required<string>();
  readonly companyId = input<string>('');

  protected readonly selectedSectionKey = signal<string | null>(null);
  protected readonly subsections = signal<AssignedSubsection[]>([]);
  private readonly driveSections = signal<DirectoryNode[]>([]);
  private readonly blueprintTree = signal<CreativeBlueprintNode[]>([]);
  private readonly isLoadingSections = signal(false);
  private readonly isLoadingBlueprint = signal(false);
  protected readonly isLoading = computed(() => this.isLoadingSections() || this.isLoadingBlueprint());
  /**
   * The selected section's sub-sections are still loading. Templates are locked
   * meanwhile: the box is empty, so already-created ones would be offered and a
   * drop would fail with 409 (Drive sibling name taken).
   */
  protected readonly isLoadingSubsections = signal(false);
  /**
   * Sub-sections whose delete is in flight, as `sectionUuid/normalizedName`. Their
   * template stays out of the pool until the delete lands — re-adding it sooner
   * races the delete into a 409 (Drive sibling name taken).
   */
  private readonly pendingDeletes = signal<ReadonlySet<string>>(new Set());

  private lastLoadedRoot: string | null = null;
  private lastLoadedBlueprint: string | null = null;
  /** In-flight section creations by section key, so concurrent drops share one create. */
  private readonly sectionCreates = new Map<string, Observable<DirectoryNode>>();
  /**
   * Placeholders of in-flight sub-section creates by section key. Switching tabs
   * replaces the box, so they are put back when the section is shown again —
   * otherwise its template is offered again and a second drop fails with 409.
   */
  private readonly pendingCreates = new Map<string, AssignedSubsection[]>();
  /** Bumped per section selection; only the latest sub-section listing is applied. */
  private subsectionsLoadSeq = 0;

  /** Blueprint sections (created in Drive or not) in blueprint order, then Drive-only sections. */
  protected readonly sections = computed<StageSection[]>(() =>
    mergeStageSections(this.driveSections(), this.blueprintTree())
  );

  protected readonly selectedSection = computed<StageSection | null>(
    () => this.sections().find((s) => s.key === this.selectedSectionKey()) ?? null
  );

  /** Sub-section templates for the active section, minus already-assigned/excluded ones. */
  protected readonly draggables = computed<CreativeBlueprintNode[]>(() => {
    const section = this.selectedSection();
    const templates = section?.blueprintNode?.children ?? [];
    const assigned = new Set(this.subsections().map((s) => normalizeStageName(s.name)));
    const deleting = this.pendingDeletes();
    const sectionUuid = section?.directory?.directoryUuid ?? '';
    return templates.filter((t) => {
      const name = normalizeStageName(t.blueprintNodeName);
      return (
        !assigned.has(name) &&
        !deleting.has(`${sectionUuid}/${name}`) &&
        !EXCLUDED_TEMPLATE_KEYS.has(t.blueprintNodeKey)
      );
    });
  });

  constructor() {
    // One effect for both sources: both loading flags are raised before either
    // request starts, so the first section is picked from the complete merged list.
    effect(() => {
      const root = this.rootId();
      const projectId = this.projectId();
      const companyId = this.companyId();
      const blueprintKey = `${projectId}:${companyId}`;
      const loadSections = !!root && root !== this.lastLoadedRoot;
      const loadBlueprint = !!projectId && !!companyId && blueprintKey !== this.lastLoadedBlueprint;
      if (loadSections) {
        this.lastLoadedRoot = root;
        this.isLoadingSections.set(true);
      }
      if (loadBlueprint) {
        this.lastLoadedBlueprint = blueprintKey;
        this.isLoadingBlueprint.set(true);
      }
      untracked(() => {
        if (loadSections) this.loadSections(root);
        if (loadBlueprint) this.loadBlueprint(projectId, companyId);
      });
    });
  }

  protected selectSection(key: string | null): void {
    if (key === this.selectedSectionKey()) return;
    this.selectedSectionKey.set(key);
    this.subsections.set(this.pendingCreatesOf(key));
    const seq = ++this.subsectionsLoadSeq;
    // A section not yet created in Drive has no sub-sections to load.
    const directoryUuid = this.selectedSection()?.directory?.directoryUuid;
    this.isLoadingSubsections.set(!!directoryUuid);
    if (!directoryUuid) return;
    this.facade
      .listSubsections(directoryUuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (subs) => this.onSubsectionsLoaded(seq, subs),
        error: () => this.onSubsectionsLoaded(seq, []),
      });
  }

  /** Template translation helper — the `translate` pipe is not standalone-importable here. */
  protected tr(key: string, params?: Record<string, unknown>): string {
    return this.translate.transform(key, params);
  }

  protected onDrop(event: CdkDragDrop<AssignedSubsection[]>): void {
    if (this.isLoading() || this.isLoadingSubsections()) return;

    // Same box → reorder. Optimistically move the item, then persist the new
    // order via the drive children/reorder endpoint so it survives a reload.
    if (event.previousContainer === event.container) {
      if (event.previousIndex === event.currentIndex) return;
      const sectionUuid = this.selectedSection()?.directory?.directoryUuid;
      if (!sectionUuid) return;
      const previous = this.subsections();
      const next = [...previous];
      moveItemInArray(next, event.previousIndex, event.currentIndex);
      this.subsections.set(next);
      this.persistOrder(sectionUuid, next, previous);
      return;
    }

    const node = event.item.data as CreativeBlueprintNode | undefined;
    const section = this.selectedSection();
    if (!node || !section) return;
    this.createSubsection(section, node, event.currentIndex);
  }

  protected promptDelete(sub: AssignedSubsection): void {
    if (!sub.directoryUuid) return;
    this.notificationService
      .open({
        title: this.translate.transform('project_stages_deleteSubsection'),
        description: this.translate.transform('project_stages_confirmDeleteSubsection', { name: sub.name }),
        confirmBtn: this.translate.transform('yes'),
        cancelBtn: this.translate.transform('no'),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirm) => {
        if (confirm) this.deleteSubsection(sub);
      });
  }

  private loadSections(root: string): void {
    this.facade
      .listSections(root)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (sections) => this.onSectionsLoaded(sections),
        error: () => this.onSectionsLoaded([]),
      });
  }

  private onSectionsLoaded(sections: DirectoryNode[]): void {
    this.driveSections.set(sections);
    this.isLoadingSections.set(false);
    this.selectFirstSectionIfNone();
  }

  private loadBlueprint(projectId: string, companyId: string): void {
    this.projectSectionService
      .getCreativeBlueprint(projectId, companyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blueprint) => this.onBlueprintLoaded(blueprint?.tree ?? []),
        error: () => this.onBlueprintLoaded([]),
      });
  }

  private onBlueprintLoaded(tree: CreativeBlueprintNode[]): void {
    this.blueprintTree.set(tree);
    this.isLoadingBlueprint.set(false);
    this.selectFirstSectionIfNone();
  }

  /**
   * Shows the loaded sub-sections, keeping what the box gained that the listing
   * may predate. While loading, the box holds only this section's in-flight
   * placeholders and the creates that finished meanwhile (drops are locked), so
   * each is kept unless the listing has it — a placeholder by name (its create
   * committed before the read), a created one by uuid. Ignored when superseded
   * by a later selection.
   */
  private onSubsectionsLoaded(seq: number, subs: DirectoryNode[]): void {
    if (seq !== this.subsectionsLoadSeq) return;
    const loaded = subs.map((d) => this.toAssigned(d));
    const listedNames = new Set(loaded.map((s) => normalizeStageName(s.name)));
    const listedUuids = new Set(loaded.map((s) => s.directoryUuid));
    const unlisted = this.subsections().filter((s) =>
      s.pending ? !listedNames.has(normalizeStageName(s.name)) : !listedUuids.has(s.directoryUuid)
    );
    this.subsections.set([...loaded, ...unlisted]);
    this.isLoadingSubsections.set(false);
  }

  private pendingCreatesOf(key: string | null): AssignedSubsection[] {
    return key ? (this.pendingCreates.get(key) ?? []) : [];
  }

  private setPendingCreate(key: string, placeholder: AssignedSubsection, inFlight: boolean): void {
    const others = this.pendingCreatesOf(key).filter((p) => p !== placeholder);
    const next = inFlight ? [...others, placeholder] : others;
    if (next.length) this.pendingCreates.set(key, next);
    else this.pendingCreates.delete(key);
  }

  /** Once both sources have settled, select the first section unless a still-listed one is selected. */
  private selectFirstSectionIfNone(): void {
    if (this.isLoading() || this.selectedSection()) return;
    this.selectSection(this.sections()[0]?.key ?? null);
  }

  private createSubsection(section: StageSection, node: CreativeBlueprintNode, index: number): void {
    const optimistic: AssignedSubsection = { directoryUuid: '', name: node.blueprintNodeName, pending: true };
    // Insert at the drop position (not appended) so the item lands where the
    // user released it, then persist the resulting order once it has a real uuid.
    this.subsections.update((arr) => {
      const next = [...arr];
      next.splice(Math.min(index, next.length), 0, optimistic);
      return next;
    });
    this.setPendingCreate(section.key, optimistic, true);
    this.ensureSectionDirectory(section)
      .pipe(
        switchMap((sectionDir) =>
          this.facade
            .createSubsection(sectionDir.directoryUuid, {
              name: node.blueprintNodeName,
              blueprintNodeUuid: node.blueprintNodeUuid,
            })
            .pipe(map((dir) => ({ sectionUuid: sectionDir.directoryUuid, dir })))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ sectionUuid, dir }) => {
          this.setPendingCreate(section.key, optimistic, false);
          // The box now shows another section — its order is not ours to persist.
          // Re-selecting this section reloads its sub-sections from Drive.
          if (this.selectedSectionKey() !== section.key) return;
          const next = this.reconcileCreated(this.subsections(), optimistic, dir);
          this.subsections.set(next);
          // The sub-section exists either way, so a failed reorder keeps `next`: rolling
          // back to the pre-create list would leave the pending spinner up for good.
          this.persistOrder(sectionUuid, next, next);
        },
        error: () => {
          this.setPendingCreate(section.key, optimistic, false);
          this.subsections.update((arr) => arr.filter((s) => s !== optimistic));
          this.overlayService.showError(this.translate.transform('projectStages_failedToSaveSubsection'));
        },
      });
  }

  /**
   * The box with a finished create applied: the placeholder becomes the created
   * sub-section; when a reload already listed it, the placeholder is just dropped;
   * when neither is shown, it is appended so it can't be offered (and created) again.
   */
  private reconcileCreated(
    current: AssignedSubsection[],
    placeholder: AssignedSubsection,
    dir: DirectoryNode
  ): AssignedSubsection[] {
    if (current.some((s) => s.directoryUuid === dir.directoryUuid)) return current.filter((s) => s !== placeholder);
    if (current.includes(placeholder)) return current.map((s) => (s === placeholder ? this.toAssigned(dir) : s));
    return [...current, this.toAssigned(dir)];
  }

  /**
   * The section's Drive directory, creating it under the project root first when
   * it exists only in the blueprint. Concurrent drops into the same uncreated
   * section share one create. A newly created section is merged into
   * {@link driveSections} and the sections' order is persisted.
   */
  private ensureSectionDirectory(section: StageSection): Observable<DirectoryNode> {
    // Re-read: an earlier drop may have created it since `section` was captured.
    const current = this.sections().find((s) => s.key === section.key) ?? section;
    if (current.directory) return of(current.directory);
    const node = current.blueprintNode;
    if (!node) return throwError(() => new Error(`Section ${section.key} has neither a directory nor a blueprint node`));
    const root = this.rootId();
    if (!root) return throwError(() => new Error('Project has no Drive root directory to create a section under'));

    let create$ = this.sectionCreates.get(section.key);
    if (!create$) {
      create$ = this.facade
        .createSection(root, { name: node.blueprintNodeName, blueprintNodeUuid: node.blueprintNodeUuid })
        .pipe(
          tap((dir) => {
            this.driveSections.update((arr) => [...arr, dir]);
            this.persistSectionOrder();
          }),
          finalize(() => this.sectionCreates.delete(section.key)),
          shareReplay(1)
        );
      this.sectionCreates.set(section.key, create$);
    }
    return create$;
  }

  /**
   * Persist the sections' merged (blueprint) order on the project root, so the
   * project nav and explorer list a newly created section in its blueprint
   * position instead of last. Best-effort: this panel orders by the blueprint
   * regardless, so a failure only leaves the nav order stale.
   */
  private persistSectionOrder(): void {
    const items: ReorderChildInput[] = this.sections()
      .flatMap((s) => (s.directory ? [s.directory.directoryUuid] : []))
      .map((directoryUuid, index) => ({ directoryUuid, displayOrder: index }));
    if (items.length < 2) return;
    // Not tied to the component lifecycle: a single finite request that must
    // still land if the user leaves while the section create is in flight.
    this.facade.reorderSections(this.rootId(), items).subscribe({ error: () => undefined });
  }

  /**
   * Persist the current sub-section order to Drive. Only already-created children
   * (real `directoryUuid`, not pending) are sent; `displayOrder` is their 0-based
   * position among those. On failure, roll back to the pre-drag order.
   */
  private persistOrder(sectionUuid: string, ordered: AssignedSubsection[], previous: AssignedSubsection[]): void {
    const items: ReorderChildInput[] = ordered
      .filter((s) => !s.pending && !!s.directoryUuid)
      .map((s, index) => ({ directoryUuid: s.directoryUuid, displayOrder: index }));
    if (items.length === 0) return;
    this.facade
      .reorderSubsections(sectionUuid, items)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.subsections.set(previous);
          this.overlayService.showError(this.translate.transform('projectStages_failedToSaveSubsection'));
        },
      });
  }

  private deleteSubsection(sub: AssignedSubsection): void {
    const previous = this.subsections();
    const deleteKey = `${this.selectedSection()?.directory?.directoryUuid ?? ''}/${normalizeStageName(sub.name)}`;
    this.subsections.update((arr) => arr.filter((s) => s.directoryUuid !== sub.directoryUuid));
    this.pendingDeletes.update((keys) => new Set(keys).add(deleteKey));
    this.facade
      .deleteSubsection(sub.directoryUuid)
      .pipe(
        finalize(() =>
          this.pendingDeletes.update((keys) => {
            const next = new Set(keys);
            next.delete(deleteKey);
            return next;
          })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: () => {
          this.subsections.set(previous);
          this.overlayService.showError(this.translate.transform('project_stages_deleteSubsectionFailed'));
        },
      });
  }

  private toAssigned(dir: DirectoryNode): AssignedSubsection {
    return { directoryUuid: dir.directoryUuid, name: directoryLabel(dir), pending: false };
  }
}
